import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import MetricsCard from './components/MetricsCard';
import BlockchainStatusCard from './components/BlockchainStatusCard';
import QualityTrendsChart from './components/QualityTrendsChart';
import EquipmentStatusGrid from './components/EquipmentStatusGrid';
import CriticalNotificationsPanel from './components/CriticalNotificationsPanel';
import RecentTransactionsTable from './components/RecentTransactionsTable';
import QuickActionButtons from './components/QuickActionButtons';

const QualityDashboard = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('7d');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  // Mock real-time data
  const [dashboardMetrics, setDashboardMetrics] = useState({
    activeBatchTests: { value: 23, change: '+12%', changeType: 'positive' },
    pendingApprovals: { value: 8, change: '-25%', changeType: 'positive' },
    complianceScore: { value: '96.8%', change: '+2.1%', changeType: 'positive' },
    blockchainTransactions: { value: 1247, change: '+18', changeType: 'positive' }
  });

  useEffect(() => {
    // Simulate real-time updates
    const interval = setInterval(() => {
      setDashboardMetrics(prev => ({
        ...prev,
        blockchainTransactions: {
          ...prev?.blockchainTransactions,
          value: prev?.blockchainTransactions?.value + Math.floor(Math.random() * 3)
        }
      }));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const handleMetricClick = (metricType) => {
    switch (metricType) {
      case 'batch_tests': navigate('/batch-testing-records');
        break;
      case 'approvals': navigate('/regulatory-compliance');
        break;
      case 'compliance': navigate('/audit-trail-viewer');
        break;
      case 'transactions': navigate('/audit-trail-viewer');
        break;
      default:
        break;
    }
  };

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'lab_a', label: 'Laboratory A' },
    { value: 'lab_b', label: 'Laboratory B' },
    { value: 'lab_c', label: 'Laboratory C' },
    { value: 'quality_control', label: 'Quality Control' },
    { value: 'regulatory', label: 'Regulatory Affairs' }
  ];

  const dateRangeOptions = [
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Quality Dashboard</h1>
              <p className="text-muted-foreground mt-2">
                Real-time pharmaceutical quality assurance monitoring and blockchain verification
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Filters */}
              <div className="flex items-center space-x-3">
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e?.target?.value)}
                  className="text-sm border border-border rounded-md px-3 py-2 bg-input text-foreground"
                >
                  {departmentOptions?.map(option => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
                
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e?.target?.value)}
                  className="text-sm border border-border rounded-md px-3 py-2 bg-input text-foreground"
                >
                  {dateRangeOptions?.map(option => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <Button
                variant="outline"
                onClick={handleRefresh}
                loading={refreshing}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Key Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricsCard
              title="Active Batch Tests"
              value={dashboardMetrics?.activeBatchTests?.value}
              change={dashboardMetrics?.activeBatchTests?.change}
              changeType={dashboardMetrics?.activeBatchTests?.changeType}
              icon="FlaskConical"
              color="bg-primary"
              description="Currently in testing phase"
              onClick={() => handleMetricClick('batch_tests')}
            />
            
            <MetricsCard
              title="Pending Approvals"
              value={dashboardMetrics?.pendingApprovals?.value}
              change={dashboardMetrics?.pendingApprovals?.change}
              changeType={dashboardMetrics?.pendingApprovals?.changeType}
              icon="Clock"
              color="bg-warning"
              description="Awaiting QA manager review"
              onClick={() => handleMetricClick('approvals')}
            />
            
            <MetricsCard
              title="Compliance Score"
              value={dashboardMetrics?.complianceScore?.value}
              change={dashboardMetrics?.complianceScore?.change}
              changeType={dashboardMetrics?.complianceScore?.changeType}
              icon="CheckCircle"
              color="bg-success"
              description="Overall regulatory compliance"
              onClick={() => handleMetricClick('compliance')}
            />
            
            <MetricsCard
              title="Blockchain Transactions"
              value={dashboardMetrics?.blockchainTransactions?.value}
              change={`+${dashboardMetrics?.blockchainTransactions?.change}`}
              changeType={dashboardMetrics?.blockchainTransactions?.changeType}
              icon="Blocks"
              color="bg-accent"
              description="Immutable records logged today"
              onClick={() => handleMetricClick('transactions')}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Left Column - Charts and Analytics */}
            <div className="lg:col-span-2 space-y-8">
              <QualityTrendsChart />
              <EquipmentStatusGrid />
              <RecentTransactionsTable />
            </div>

            {/* Right Column - Status and Notifications */}
            <div className="space-y-8">
              <BlockchainStatusCard />
              <CriticalNotificationsPanel />
            </div>
          </div>

          {/* Quick Actions */}
          <QuickActionButtons />

          {/* Footer Info */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <div className="flex items-center space-x-4">
                <span>Last updated: {new Date()?.toLocaleTimeString('en-GB', { hour12: false })}</span>
                <span>•</span>
                <span>Data refresh interval: 30 seconds</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span>All data cryptographically verified</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QualityDashboard;